import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import api from '../../services/api';

interface Config {
  plan_monthly_cents: Record<string, number>;
  default_monthly_cents: number;
  packs: Record<string, { credits: number; price_brl: number; label: string; popular?: boolean }>;
  estimates: Record<string, number>;
  default_estimate_cents: number;
}

const paraReais = (c: number) => (c / 100).toFixed(2);
const deReais = (v: string) => Math.max(0, Math.round(parseFloat(String(v).replace(',', '.')) * 100) || 0);

/**
 * Configuração da cobrança de IA (super-admin): franquia mensal de cada plano e pacotes de
 * recarga. A franquia de uma conta específica ("limite a mais") fica no extrato da conta.
 */
const AiBillingConfigCard: React.FC = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<Config | null>(null);
  const [planos, setPlanos] = useState<Record<string, string>>({});
  const [semPlano, setSemPlano] = useState('');
  const [pacotes, setPacotes] = useState<Record<string, { creditos: string; preco: string; label: string; popular?: boolean }>>({});
  const [salvando, setSalvando] = useState(false);

  const preencher = (c: Config) => {
    setConfig(c);
    setPlanos(Object.fromEntries(Object.entries(c.plan_monthly_cents).map(([k, v]) => [k, paraReais(v)])));
    setSemPlano(paraReais(c.default_monthly_cents));
    setPacotes(Object.fromEntries(Object.entries(c.packs).map(([k, p]) => [k, { creditos: String(p.credits), preco: String(p.price_brl), label: p.label, popular: p.popular }])));
  };

  useEffect(() => {
    api.get('/admin/ai-credits/config').then((r) => r.data?.success && preencher(r.data.data)).catch(() => toast.error(t('errors.generic')));
  }, [t]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const body = {
        plan_monthly_cents: Object.fromEntries(Object.entries(planos).map(([k, v]) => [k, deReais(v)])),
        default_monthly_cents: deReais(semPlano),
        packs: Object.fromEntries(Object.entries(pacotes).map(([k, p]) => [k, { credits: parseInt(p.creditos, 10) || 0, price_brl: parseFloat(p.preco.replace(',', '.')) || 0, label: p.label, popular: !!p.popular }]))
      };
      const r = await api.put('/admin/ai-credits/config', body);
      if (r.data?.success) { preencher(r.data.data); toast.success(t('admin.aiConfigSaved', 'Configuração de IA salva')); }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('errors.generic'));
    } finally {
      setSalvando(false);
    }
  };

  if (!config) return <Card><CardContent className="p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></CardContent></Card>;

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('admin.aiConfigTitle', 'Franquia por plano e pacotes de recarga')}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('admin.aiConfigHelp', 'Valores em reais (1 crédito = R$ 0,01). A franquia renova todo mês; pacote comprado não vence. Limite a mais de uma conta: no extrato da conta.')}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {Object.keys(planos).map((slug) => (
            <Input key={slug} label={slug} type="number" min="0" step="0.01" value={planos[slug]} onChange={(e) => setPlanos({ ...planos, [slug]: e.target.value })} />
          ))}
          <Input label={t('admin.aiNoPlan', 'sem assinatura')} type="number" min="0" step="0.01" value={semPlano} onChange={(e) => setSemPlano(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(pacotes).map(([id, p]) => (
            <div key={id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{p.label} ({id})</p>
              <Input label={t('admin.aiPackCredits', 'Créditos')} type="number" min="1" value={p.creditos} onChange={(e) => setPacotes({ ...pacotes, [id]: { ...p, creditos: e.target.value } })} />
              <Input label={t('admin.aiPackPrice', 'Preço (R$)')} type="number" min="1" step="0.01" value={p.preco} onChange={(e) => setPacotes({ ...pacotes, [id]: { ...p, preco: e.target.value } })} />
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button onClick={salvar} disabled={salvando} loading={salvando}>{t('common.save', 'Salvar')}</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AiBillingConfigCard;
